import React, { useState } from 'react';
import cx from 'classnames';
import { Link } from 'react-router';

import Button from '~/components/button';
import { SeasonalShipping } from '~/components/cart/cart-item';
import IncrementorField from '~/components/inputs/incrementor-field';
import LoadingIndicator from '~/components/loading-indicator';
import Modal from '~/components/modals/modal';
import RecommendedProductsList from '~/components/sunday-store/components/product-details/recommended-products-list';
import { cartPurchaseOrderSelector } from '~/features/cart/cart';
import { useCartMutations } from '~/features/cart/cart-actions';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { useLawn } from '~/features/lawns/lawn-actions';
import { useProductData } from '~/features/products/products-actions';
import { useCart } from '~/hooks/use-cart';
import { cartRoute, shopAllRoute } from '~/routes';
import { urlFor } from '~/sanity/images';
import { captureException } from '~/utils/exception-tracking';
import { centsToCurrency } from '~/utils/numbers';
import {
  showGladlyChat,
  hideGladlyChat,
} from '~/utils/analytics/plugins/gladly';
import { CHANNEL_NON_FUNNEL } from '~/utils/channels';
import getChannelAndPrice from '~/utils/get-channel-price';
import { subscriptionType } from '~/features/subscriptions/subscription';
import { canSeeQtyField } from '~/features/products/products';

import modalStyles from '~/components/modals/modal.module.scss';
import styles from '~/components/modals/added-to-cart/added-to-cart-modal.module.scss';

const DEFAULT_PLAN_IMAGE = '/images/unboxing-bottom-400w.png?v1';

const AddedToCartModal = ({
  isOpen,
  onClose,
  product,
  showRecommendedProducts = true,
}) => {
  if (isOpen) {
    hideGladlyChat();
  }

  const handleClose = () => {
    showGladlyChat();
    onClose();
  };

  return (
    <Modal
      onClose={handleClose}
      isOpen={isOpen}
      className={cx(modalStyles.slideOutModal, styles.modalContent)}
    >
      <AddedToCartModalContent
        product={product}
        onClose={handleClose}
        showRecommendedProducts={showRecommendedProducts}
      />
    </Modal>
  );
};

function AddedToCartModalContent({
  product,
  onClose,
  showRecommendedProducts,
}) {
  const {
    products,
    recommendedProducts,
    isLoading: isLoadingProductData,
  } = useProductData({ slug: product?.productDetails.slug });
  const { cart, bundledCartItems, isLoading: isLoadingCart } = useCart();
  const { lawn, isLoading: isLoadingLawn } = useLawn();

  const masterCart = cartPurchaseOrderSelector(cart);

  const { addons, ...cartBundles } = bundledCartItems;

  const isLoading =
    isLoadingCart ||
    !cart ||
    !masterCart ||
    isLoadingProductData ||
    !product ||
    isLoadingLawn;

  let addOnProducts = [];

  if (!isLoading) {
    addOnProducts = addons
      ? addons
          .map((cartItem) => {
            const productInfo = products.find(
              (product) => product.productDetails.sku === cartItem.sku.skuId
            );

            if (!productInfo) {
              return null;
            }

            return {
              quantity: cartItem.quantity,
              cartItem,
              ...productInfo,
            };
          })
          .filter(Boolean)
      : [];
  }

  return (
    <>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <h1 className={modalStyles.title}>Added to cart!</h1>
          <div className={styles.modalInnerContent}>
            {cart.containsLawnPlan && (
              <BundleItem
                cart={cart}
                price={masterCart.bundlePrice}
                lawn={lawn}
                planCartItems={Object.values(cartBundles)[0]}
              />
            )}
            {addOnProducts.map((item) => (
              <AddOnProduct product={item} key={item.purchaseSku.skuId} />
            ))}
            {cart?.contents.length === 0 && (
              <div className={styles.emptyCartMessage}>
                <p>You have no items in your cart.</p>
                <p>
                  <Link to={shopAllRoute()}>Shop all Sunday</Link>
                </p>
              </div>
            )}
            {showRecommendedProducts && recommendedProducts?.length > 0 && (
              <RecommendedProductsList
                products={recommendedProducts}
                title={product?.productDetails?.relatedProducts?.heading}
                onLinkClick={onClose}
              />
            )}
          </div>
          <div className={styles.footer}>
            <p className={styles.subtotal}>
              Subtotal: <span>{centsToCurrency(masterCart?.subtotal)}</span>
            </p>
            <Button
              fullWidth
              to={cartRoute()}
              onClick={onClose}
              data-testid="added-to-cart-checkout-button"
            >
              Checkout
            </Button>
          </div>
        </>
      )}
    </>
  );
}

const BundleItem = ({ cart, price, lawn, planCartItems }) => {
  const { removeMultipleFromCart } = useCartMutations();
  const [isDeleting, setIsDeleting] = useState(false);
  const { showFlashMessage } = useFlashMessage();

  const masterCart = cartPurchaseOrderSelector(cart);

  const title = cart.bundleTitle;

  const handleRemoveItems = async () => {
    try {
      setIsDeleting(true);

      await removeMultipleFromCart([...planCartItems]);

      showFlashMessage(
        `${lawn?.currentPlanYear} ${title} plan removed from your cart.`,
        'info',
        2500
      );
    } catch (err) {
      setIsDeleting(false);
      showFlashMessage(
        `Oops, there was a problem removing the ${title} plan from your cart.`,
        'error',
        5000
      );

      captureException(
        new CartDrawerError('Lawn Plan Item - handleRemoveItems'),
        {
          extras: {
            error: JSON.stringify(err),
          },
        }
      );
    }
  };
  return (
    <div className={styles.cartItem}>
      <img
        className={styles.productImg}
        src={DEFAULT_PLAN_IMAGE}
        alt={title + ' Smart Lawn Plan'}
      />
      <div>
        <p className={styles.title}>
          {lawn?.currentPlanYear} {title} plan
        </p>
        {isDeleting ? (
          <div className={styles.cartItemUpdatingLoader}>
            <LoadingIndicator />
          </div>
        ) : (
          <p className={styles.price}>
            {centsToCurrency(price || masterCart.bundlePrice)}
          </p>
        )}
        <div className={styles.controls}>
          <Button
            className={styles.removeBtn}
            variant="link"
            onClick={handleRemoveItems}
            isLoading={isDeleting}
            aria-label={`Remove ${lawn?.currentPlanYear} ${title} plan
          from cart`}
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
};

const AddOnProduct = ({ product }) => {
  const { showFlashMessage } = useFlashMessage();
  const { updateCartQuantity, removeFromCart } = useCartMutations();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { purchaseSku, productDetails, quantity, cartItem } = product;
  const { unitPrice } = getChannelAndPrice(CHANNEL_NON_FUNNEL, product);

  const isPlan =
    product.purchaseSku.isPlan ||
    [
      subscriptionType.LAWN_PLAN,
      subscriptionType.MOSQUITO,
      subscriptionType.TICK,
      subscriptionType.TOTAL_HOME,
      subscriptionType.SPRING,
    ].includes(product?.purchaseSku?.subscriptionType);

  const canEditQuantity = canSeeQtyField(productDetails, isPlan);

  const handleUpdateQuantity = async (qty) => {
    try {
      setIsUpdating(true);
      await updateCartQuantity(cartItem, qty);
    } catch (err) {
      showFlashMessage(
        `Oops, there was a problem updating the quantity for ${cartItem.sku.title}.`,
        'error',
        5000
      );
      captureException(
        new CartDrawerError('Add-On Item - handleUpdateQuantity'),
        {
          extras: {
            error: JSON.stringify(err),
          },
        }
      );
    }

    setIsUpdating(false);
  };

  const handleRemoveItem = async () => {
    try {
      setIsDeleting(true);
      await removeFromCart(cartItem);
    } catch (err) {
      setIsDeleting(false);
      showFlashMessage(
        `Oops, there was a problem removing ${cartItem.sku.title} from your cart.`,
        'error',
        5000
      );

      captureException(new CartDrawerError('Add-On Item - handleRemoveItems'), {
        extras: {
          error: JSON.stringify(err),
        },
      });
    }
  };

  return (
    <div className={styles.cartItem}>
      <img
        className={styles.productImg}
        src={
          productDetails?.primaryImage
            ? urlFor(productDetails.primaryImage)
                .width(127)
                .height(127)
                .crop('focalpoint')
            : DEFAULT_PLAN_IMAGE
        }
        alt={productDetails?.primaryImage?.alt || 'Sunday Smart Lawn Plan'}
      />
      <div>
        <p className={styles.title}>
          {productDetails?.name || purchaseSku.title}
        </p>
        <div className={styles.seasonalShippingWrapper}>
          <SeasonalShipping
            className={styles.shippingMessage}
            cartItemSku={cartItem?.sku}
          />
        </div>
        {isUpdating ? (
          <div className={styles.cartItemUpdatingLoader}>
            <LoadingIndicator />
          </div>
        ) : (
          <p className={styles.price}>{centsToCurrency(unitPrice)}</p>
        )}
        <div className={styles.controls}>
          {canEditQuantity && (
            <IncrementorField
              size="small"
              input={{
                onChange: (e) => handleUpdateQuantity(e.target.value),
                value: quantity,
                disabled: isUpdating,
              }}
            />
          )}
          <Button
            className={styles.removeBtn}
            variant="link"
            onClick={handleRemoveItem}
            isLoading={isDeleting}
            aria-label={`Remove ${
              cartItem?.sku?.title || 'Smart Lawn Plan'
            } from cart`}
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddedToCartModal;

class CartDrawerError extends Error {
  constructor(message) {
    super(message);
    this.name = 'CartDrawerError';
  }
}
